import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSettings,
  putSetting,
  getFitbitStatus,
  connectFitbit,
  syncFitbit,
  disconnectFitbit,
  getSyncStatus,
  type FitbitStatus,
  type SyncSource,
} from '../api.js';
import { relativeAgo } from '../lib/freshness.js';
import {
  reorder,
  normalizePriority,
  sourceLabel,
  profileToForm,
  formToProfile,
  homeLocationToForm,
  formToHomeLocation,
  type ProfileForm,
  type HomeLocationForm,
} from '../lib/settingsForm.js';
import { readTheme, setTheme, type ThemePref } from '../lib/theme.js';
import { cmToFtIn, ftInToCm, parseUnitPref, type UnitPref } from '../lib/units.js';
import { Group, Icon, IconTile, Page, Row, useToast } from '../ui/index.js';

function fmtTime(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// --- Appearance ---------------------------------------------------------------

const THEMES: readonly { value: ThemePref; label: string }[] = [
  { value: 'system', label: 'Automatic' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** iOS Display & Brightness: three previews, the chosen one ticked. Per device. */
function AppearanceGroup(): JSX.Element {
  const [pref, setPref] = useState<ThemePref>(() => readTheme());
  function pick(p: ThemePref): void {
    setPref(p);
    setTheme(p);
  }
  return (
    <Group header="Appearance" footer="Automatic follows this device's light and dark setting.">
      <li className="ui-swipe">
        <div className="bc-theme" role="radiogroup" aria-label="Appearance">
          {THEMES.map((t) => (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={pref === t.value}
              className={`bc-theme__opt${pref === t.value ? ' bc-theme__opt--on' : ''}`}
              onClick={() => pick(t.value)}
            >
              <span className={`bc-theme__preview bc-theme__preview--${t.value}`} aria-hidden="true">
                <span className="bc-theme__bar" />
                <span className="bc-theme__bar bc-theme__bar--short" />
                <span className="bc-theme__card" />
              </span>
              <span className="bc-theme__label">{t.label}</span>
              <span className="bc-theme__radio" aria-hidden="true">
                {pref === t.value && <Icon name="check" size={13} />}
              </span>
            </button>
          ))}
        </div>
      </li>
    </Group>
  );
}

// --- Google Health (Fitbit data via Google) --------------------------------------

function GoogleHealthGroup({
  status,
  onChanged,
}: {
  status: FitbitStatus | null;
  onChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function connect(): Promise<void> {
    setBusy('connect');
    setError(null);
    try {
      const { authUrl } = await connectFitbit();
      window.location.assign(authUrl);
    } catch {
      setError('Could not start the Google Health connection.');
      setBusy(null);
    }
  }

  async function runSync(): Promise<void> {
    setBusy('sync');
    setError(null);
    try {
      const s = await syncFitbit();
      toast.info(
        s.ran
          ? `Synced ${s.typesSynced} data type(s): +${s.inserted.metrics} metrics, +${s.inserted.sleep} sleep${s.reason ? ` (${s.reason})` : ''}`
          : `Sync skipped${s.reason ? `: ${s.reason}` : ''}`,
      );
      onChanged();
    } catch {
      setError('Sync failed. Try again shortly.');
    } finally {
      setBusy(null);
    }
  }

  async function disconnect(): Promise<void> {
    setBusy('disconnect');
    setError(null);
    try {
      await disconnectFitbit();
      toast.info('Google Health disconnected.');
      onChanged();
    } catch {
      setError('Could not disconnect Google Health.');
    } finally {
      setBusy(null);
    }
  }

  const connected = status?.connected ?? false;
  const state =
    status === null
      ? '…'
      : !status.enabled
        ? 'Not set up'
        : connected
          ? 'Connected'
          : status.disconnected
            ? 'Disconnected'
            : 'Not connected';

  const footer = error ? (
    <span className="bc-bad">{error}</span>
  ) : status && !status.enabled ? (
    'Set GHEALTH_CLIENT_ID and GHEALTH_CLIENT_SECRET on the server to turn on Google Health sync.'
  ) : (
    'Sleep, heart rate and activity from your Fitbit, through Google.'
  );

  return (
    <Group header="Google Health" footer={footer}>
      <Row
        leading={<IconTile name="heart" color="#ff375f" />}
        title="Google Health"
        detail={<span className={connected ? 'bc-good' : undefined}>{state}</span>}
      />
      {connected && status && <Row title="Last sync" detail={fmtTime(status.lastSyncAt)} />}
      {connected && status && <Row title="Synced through" detail={status.cursor ?? '—'} />}
      {connected && status?.nextAllowedAt && <Row title="Paused until" detail={fmtTime(status.nextAllowedAt)} />}
      {connected ? (
        <Row tone="accent" title={busy === 'sync' ? 'Syncing…' : 'Sync now'} onClick={busy ? undefined : () => void runSync()} />
      ) : (
        status?.enabled && (
          <Row
            tone="accent"
            title={busy === 'connect' ? 'Opening Google…' : 'Connect Google Health'}
            onClick={busy ? undefined : () => void connect()}
          />
        )
      )}
      {connected && (
        <Row
          tone="bad"
          title={busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
          onClick={busy ? undefined : () => void disconnect()}
        />
      )}
    </Group>
  );
}

// --- Units -------------------------------------------------------------------------

const UNITS: readonly { value: UnitPref; label: string; hint: string }[] = [
  { value: 'imperial', label: 'Imperial', hint: 'lb, mi, fl oz' },
  { value: 'metric', label: 'Metric', hint: 'kg, km, mL' },
];

function UnitsGroup({ units, onSave }: { units: UnitPref; onSave: (next: UnitPref) => void }): JSX.Element {
  return (
    <Group header="Units" footer="Only how numbers are shown. Your data is always stored in metric.">
      {UNITS.map((u) => (
        <li key={u.value} className="ui-swipe">
          <button
            type="button"
            className="ui-pick"
            role="radio"
            aria-checked={units === u.value}
            onClick={() => onSave(u.value)}
          >
            <span className="ui-pick__label">
              {u.label} <span className="bc-muted">{u.hint}</span>
            </span>
            {units === u.value && <Icon name="check" size={20} className="ui-pick__check" />}
          </button>
        </li>
      ))}
    </Group>
  );
}

// --- Profile --------------------------------------------------------------------------

/** Derive ft + in string fields from a canonical heightCm string ('' when blank). */
function heightToFtIn(heightCm: string): { ft: string; in: string } {
  const cm = Number(heightCm);
  if (heightCm.trim() === '' || !Number.isFinite(cm)) return { ft: '', in: '' };
  const { ft, in: inch } = cmToFtIn(cm);
  return { ft: String(ft), in: String(inch) };
}

/** Save state for an edit group: the header action reads Save / Saving… / Saved. */
function saveAction(dirty: boolean, busy: boolean, saved: boolean, onSave: () => void): { label: string; onClick?: () => void } | undefined {
  if (busy) return { label: 'Saving…' };
  if (dirty) return { label: 'Save', onClick: onSave };
  if (saved) return { label: 'Saved' };
  return undefined;
}

function ProfileGroup({
  initial,
  unitPref,
  onSave,
}: {
  initial: ProfileForm;
  unitPref: UnitPref;
  onSave: (value: Record<string, unknown>) => Promise<void>;
}): JSX.Element {
  const [form, setForm] = useState<ProfileForm>(initial);
  const [height, setHeight] = useState(() => heightToFtIn(initial.heightCm));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setForm(initial);
    setHeight(heightToFtIn(initial.heightCm));
  }, [initial]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  function field(key: keyof ProfileForm, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  /** Edit a ft/in field; keep the canonical heightCm in the form in sync. */
  function heightField(key: 'ft' | 'in', value: string): void {
    const next = { ...height, [key]: value };
    setHeight(next);
    const ft = Number(next.ft);
    const inch = Number(next.in);
    const bothBlank = next.ft.trim() === '' && next.in.trim() === '';
    const cm =
      bothBlank || !Number.isFinite(ft) || !Number.isFinite(inch)
        ? ''
        : String(Math.round(ftInToCm(ft || 0, inch || 0) * 10) / 10);
    field('heightCm', cm);
  }

  async function save(): Promise<void> {
    const value = formToProfile(form);
    if (value === null) {
      setError('Age and height must be numbers.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(value);
      setSaved(true);
    } catch {
      setError('Could not save your profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Group
      header="Profile"
      action={saveAction(dirty, busy, saved, () => void save())}
      footer={error ? <span className="bc-bad">{error}</span> : 'The coach uses these to read your numbers in context.'}
    >
      <li className="ui-swipe">
        <label className="bc-field">
          <span className="bc-field__label">Age</span>
          <input className="bc-field__input" inputMode="numeric" placeholder="Years" value={form.age} onChange={(e) => field('age', e.target.value)} />
        </label>
      </li>
      <li className="ui-swipe">
        {unitPref === 'imperial' ? (
          <div className="bc-field">
            <span className="bc-field__label">Height</span>
            <span className="bc-field__pair">
              <input
                className="bc-field__input bc-field__input--short"
                inputMode="numeric"
                placeholder="5"
                aria-label="Height feet"
                value={height.ft}
                onChange={(e) => heightField('ft', e.target.value)}
              />
              <span className="bc-muted">ft</span>
              <input
                className="bc-field__input bc-field__input--short"
                inputMode="numeric"
                placeholder="10"
                aria-label="Height inches"
                value={height.in}
                onChange={(e) => heightField('in', e.target.value)}
              />
              <span className="bc-muted">in</span>
            </span>
          </div>
        ) : (
          <label className="bc-field">
            <span className="bc-field__label">Height</span>
            <input className="bc-field__input" inputMode="numeric" placeholder="cm" value={form.heightCm} onChange={(e) => field('heightCm', e.target.value)} />
          </label>
        )}
      </li>
      <li className="ui-swipe">
        <label className="bc-field bc-field--stack">
          <span className="bc-field__label">Goals</span>
          <input
            className="bc-field__input"
            placeholder="Run a half marathon, sleep 8h"
            value={form.goals}
            onChange={(e) => field('goals', e.target.value)}
          />
        </label>
      </li>
    </Group>
  );
}

// --- Home location (weather / pollen) ------------------------------------------------

function HomeLocationGroup({
  initial,
  onSave,
}: {
  initial: HomeLocationForm;
  onSave: (value: { lat: number; lon: number }) => Promise<void>;
}): JSX.Element {
  const [form, setForm] = useState<HomeLocationForm>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(initial), [initial]);
  const dirty = form.lat !== initial.lat || form.lon !== initial.lon;

  function field(key: keyof HomeLocationForm, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  async function save(): Promise<void> {
    const value = formToHomeLocation(form);
    if (value === null) {
      setError('Enter a valid latitude (−90…90) and longitude (−180…180).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(value);
      setSaved(true);
    } catch {
      setError('Could not save your location.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Group
      header="Home location"
      action={saveAction(dirty, busy, saved, () => void save())}
      footer={error ? <span className="bc-bad">{error}</span> : 'Used to line up weather and pollen with how you feel.'}
    >
      <li className="ui-swipe">
        <label className="bc-field">
          <span className="bc-field__label">Latitude</span>
          <input className="bc-field__input" inputMode="decimal" placeholder="45.8" aria-label="Latitude" value={form.lat} onChange={(e) => field('lat', e.target.value)} />
        </label>
      </li>
      <li className="ui-swipe">
        <label className="bc-field">
          <span className="bc-field__label">Longitude</span>
          <input className="bc-field__input" inputMode="decimal" placeholder="15.98" aria-label="Longitude" value={form.lon} onChange={(e) => field('lon', e.target.value)} />
        </label>
      </li>
    </Group>
  );
}

// --- Source priority -------------------------------------------------------------------

function SourcePriorityGroup({ priority, onSave }: { priority: string[]; onSave: (next: string[]) => void }): JSX.Element {
  return (
    <Group header="Source priority" footer="When two devices report the same thing, the higher one wins.">
      {priority.map((source, i) => (
        <li key={source} className="ui-swipe">
          <div className="ui-row">
            <span className="ui-row__lead">
              <span className="bc-rank num">{i + 1}</span>
            </span>
            <span className="ui-row__main">
              <span className="ui-row__title">{sourceLabel(source)}</span>
            </span>
            <span className="ui-row__trail bc-moves">
              <button
                type="button"
                className="bc-move"
                aria-label={`Move ${sourceLabel(source)} up`}
                disabled={i === 0}
                onClick={() => onSave(reorder(priority, i, 'up'))}
              >
                <Icon name="chevron-down" size={18} className="bc-flip" />
              </button>
              <button
                type="button"
                className="bc-move"
                aria-label={`Move ${sourceLabel(source)} down`}
                disabled={i === priority.length - 1}
                onClick={() => onSave(reorder(priority, i, 'down'))}
              >
                <Icon name="chevron-down" size={18} />
              </button>
            </span>
          </div>
        </li>
      ))}
    </Group>
  );
}

// --- Sync health -------------------------------------------------------------------------

function SyncHealthGroup(): JSX.Element {
  const [sources, setSources] = useState<SyncSource[] | null>(null);

  useEffect(() => {
    let alive = true;
    getSyncStatus()
      .then((r) => {
        if (alive) setSources(r.sources);
      })
      .catch(() => {
        if (alive) setSources([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (sources === null) {
    return (
      <Group header="Sync health">
        <Row title="Loading…" tone="muted" />
      </Group>
    );
  }
  return (
    <Group header="Sync health" footer={sources.length === 0 ? 'No sync activity recorded yet.' : 'When each source last delivered data.'}>
      {sources.map((s) => {
        const at = s.lastReceivedAt ?? s.lastSyncAt;
        return (
          <Row
            key={s.source}
            leading={<span className={`bc-status${s.lastError ? ' bc-status--bad' : at ? ' bc-status--good' : ''}`} aria-hidden="true" />}
            title={sourceLabel(s.source)}
            sub={s.lastError ? <span className="bc-bad">{s.lastError}</span> : undefined}
            detail={at ? relativeAgo(at) : 'Never'}
          />
        );
      })}
    </Group>
  );
}

// --- Page ------------------------------------------------------------------------------

export default function Settings(): JSX.Element {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<FitbitStatus | null>(null);
  const [priority, setPriority] = useState<string[]>([]);
  const [units, setUnits] = useState<UnitPref>('imperial');
  const [profile, setProfile] = useState<ProfileForm>({ age: '', heightCm: '', goals: '' });
  const [homeLocation, setHomeLocation] = useState<HomeLocationForm>({ lat: '', lon: '' });
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(() => {
    getFitbitStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const loadSettings = useCallback(() => {
    getSettings()
      .then((s) => {
        setPriority(normalizePriority(s.source_priority));
        setUnits(parseUnitPref(s.units));
        setProfile(profileToForm(s.profile));
        setHomeLocation(homeLocationToForm(s.home_location));
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load settings.'));
  }, []);

  useEffect(() => {
    loadStatus();
    loadSettings();
  }, [loadStatus, loadSettings]);

  // Surface the OAuth callback outcome (?ghealth=connected|error), then clear it.
  useEffect(() => {
    const flag = params.get('ghealth');
    if (!flag) return;
    if (flag === 'connected') toast.info('Google Health connected.');
    else toast.error('Google Health connection failed.');
    params.delete('ghealth');
    setParams(params, { replace: true });
    loadStatus();
  }, [params, setParams, loadStatus, toast]);

  function savePriority(next: string[]): void {
    setPriority(next);
    putSetting('source_priority', next).catch(() => {
      setError('Could not save source priority.');
      loadSettings();
    });
  }

  function saveUnits(next: UnitPref): void {
    if (next === units) return;
    const prev = units;
    setUnits(next);
    putSetting('units', next)
      .then(() => toast.info(`Units set to ${next}.`))
      .catch(() => {
        setUnits(prev);
        setError('Could not save units.');
      });
  }

  async function saveProfile(value: Record<string, unknown>): Promise<void> {
    await putSetting('profile', value);
    toast.info('Profile saved.');
  }

  async function saveHomeLocation(value: { lat: number; lon: number }): Promise<void> {
    await putSetting('home_location', value);
    toast.info('Home location saved.');
  }

  return (
    <Page title="Settings" className="bc-settings">
      {error && (
        <p className="bc-banner" role="alert">
          <Icon name="alert" size={18} />
          {error}
        </p>
      )}
      <AppearanceGroup />
      <Group header="Accounts" footer="Mailboxes and calendars: Outlook, Gmail, cPanel email, iCloud.">
        <Row leading={<IconTile name="mail" color="#0078d4" />} title="Mail & calendar accounts" to="/connections?tab=accounts" />
      </Group>
      <GoogleHealthGroup status={status} onChanged={loadStatus} />
      <UnitsGroup units={units} onSave={saveUnits} />
      <ProfileGroup initial={profile} unitPref={units} onSave={saveProfile} />
      <HomeLocationGroup initial={homeLocation} onSave={saveHomeLocation} />
      <SyncHealthGroup />
      <SourcePriorityGroup priority={priority} onSave={savePriority} />
    </Page>
  );
}
